import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, CheckCircle2, Loader2, Sparkles, GitBranch, Copy, AlertTriangle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const MAPPING_TYPES = {
  EXACT: "Esatto (prefill automatico)",
  PARTIAL: "Parziale (mai prefill automatico)",
  TRANSFORMABLE: "Trasformabile (conversione unità)",
  RELATED: "Correlato (solo riferimento)",
  NO_MAPPING: "Nessun mapping (dato specifico del destinatario)",
};
const QUALITY_BADGE = {
  OFFICIAL: "bg-green-700 text-white", VERIFIED_INTERNAL: "bg-green-100 text-green-800",
  PROVISIONAL: "bg-amber-100 text-amber-800", MANUAL: "bg-sky-100 text-sky-800",
};
const TPL_STATUS_LABEL = {
  DRAFT: "Bozza", IMPORTED: "Importato", MAPPING_REQUIRED: "Mapping richiesto",
  UNDER_REVIEW: "In revisione", ACTIVE: "Attivo", DEPRECATED: "Deprecato",
  ARCHIVED: "Archiviato",
};

export default function ExternalTemplateDetail() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/external/templates/${templateId}`);
      setData(data);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [templateId]);

  useEffect(() => { load(); }, [load]);

  const mappingsByQ = {};
  (data?.mappings || []).forEach((m) => { mappingsByQ[m.question_id] = m; });

  const saveMapping = async (qid, verified) => {
    const d = drafts[qid];
    if (!d) return;
    setBusy(true);
    try {
      await api.put(`/external/templates/${templateId}/mappings/${qid}`, {
        canonical_datapoint_code: d.canonical_datapoint_code || null,
        mapping_type: d.mapping_type,
        from_unit: d.from_unit || null, to_unit: d.to_unit || null,
        verified });
      toast.success(verified ? "Mapping verificato" : "Mapping salvato come provvisorio");
      setDrafts((p) => ({ ...p, [qid]: null }));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const autoMap = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/external/templates/${templateId}/auto-map`);
      toast.success(`${data.proposals_created} proposte AI create (tutte PROVISIONAL, da verificare)`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const transition = async (status) => {
    setBusy(true);
    try {
      await api.post(`/external/templates/${templateId}/transition`, { status });
      toast.success(`Stato aggiornato: ${TPL_STATUS_LABEL[status]}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const duplicate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/external/templates/${templateId}/duplicate`);
      toast.success(`Template duplicato come ${data.template.version}`);
      navigate(`/questionari-esterni/template/${data.template.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="max-w-lg mx-auto mt-16" data-testid="template-detail-error">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6 text-center text-sm text-red-700">{error}</CardContent>
        </Card>
      </div>
    );
  }
  if (!data) return <p className="text-sm text-slate-500">Caricamento…</p>;

  const tpl = data.template;
  const questions = tpl.questions || [];
  const mapped = questions.filter((q) => mappingsByQ[q.qid]).length;
  const provisional = (data.mappings || []).filter(
    (m) => m.mapping_quality === "PROVISIONAL" && ["EXACT", "TRANSFORMABLE"].includes(m.mapping_type)).length;

  return (
    <div className="space-y-6" data-testid="template-detail-page">
      <div className="flex items-start gap-3 flex-wrap">
        <Button variant="ghost" size="sm" className="h-8"
          onClick={() => navigate("/questionari-esterni")} data-testid="template-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" aria-hidden="true" />Libreria
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl md:text-2xl font-bold tracking-tight text-slate-900">
            {tpl.name}
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            {tpl.code} · {tpl.version} · {tpl.source_type} · scope {tpl.scope} ·{" "}
            {data.instances_count} questionari assegnati
          </p>
        </div>
        <Badge variant="outline" data-testid="template-status-badge">
          {TPL_STATUS_LABEL[tpl.status] || tpl.status}
        </Badge>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-wrap items-center gap-2">
          <p className="text-sm text-slate-700 mr-2" data-testid="mapping-progress">
            Mapping: <strong>{mapped}/{questions.length}</strong>
            {provisional > 0 && (
              <span className="text-amber-700"> · {provisional} proposte AI da verificare</span>)}
          </p>
          <div className="flex-1" />
          <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy}
            onClick={autoMap} data-testid="auto-map-btn">
            <Sparkles className="w-3 h-3 mr-1" aria-hidden="true" />Proponi mapping con AI
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy}
            onClick={duplicate} data-testid="duplicate-template-btn">
            <Copy className="w-3 h-3 mr-1" aria-hidden="true" />Duplica (nuova versione)
          </Button>
          {["DRAFT", "IMPORTED", "MAPPING_REQUIRED"].includes(tpl.status) && (
            <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy}
              onClick={() => transition("UNDER_REVIEW")} data-testid="to-review-btn">
              Avvia revisione
            </Button>
          )}
          {tpl.status === "UNDER_REVIEW" && (
            <Button size="sm" className="h-8 text-xs bg-green-700 hover:bg-green-800 text-white"
              disabled={busy} onClick={() => transition("ACTIVE")}
              data-testid="activate-template-btn">
              <CheckCircle2 className="w-3 h-3 mr-1" aria-hidden="true" />Attiva template
            </Button>
          )}
          {tpl.status === "ACTIVE" && (
            <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy}
              onClick={() => transition("DEPRECATED")} data-testid="deprecate-template-btn">
              Depreca
            </Button>
          )}
        </CardContent>
      </Card>

      {provisional > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 flex items-center gap-2"
          data-testid="provisional-banner">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" aria-hidden="true" />
          <p className="text-xs text-amber-900">
            I mapping proposti dall'AI restano PROVISIONAL: il prefill automatico definitivo
            richiede la tua verifica ("Salva e verifica").
          </p>
        </div>
      )}

      <div className="space-y-3">
        {questions.map((q) => {
          const m = mappingsByQ[q.qid];
          const draft = drafts[q.qid];
          const currentType = draft?.mapping_type ?? m?.mapping_type ?? "";
          const currentCode = draft?.canonical_datapoint_code ?? m?.canonical_datapoint_code ?? "";
          return (
            <Card key={q.qid} className="border-slate-200"
              data-testid={`mapping-card-${q.qid}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <span className="text-xs font-mono text-slate-400 mt-0.5">{q.qid}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 leading-relaxed max-w-prose">
                      {q.original_text}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      {q.data_type}{q.unit ? ` · ${q.unit}` : ""}{q.required ? " · obbligatoria" : ""}
                      {q.needs_review && (
                        <span className="text-amber-700"> · da revisionare (import AI)</span>)}
                    </p>
                  </div>
                  {m && (
                    <Badge className={`text-[10px] ${QUALITY_BADGE[m.mapping_quality] || ""}`}
                      data-testid={`mapping-quality-${q.qid}`}>
                      {m.mapping_quality}{m.mapping_source === "AI" ? " · AI" : ""}
                    </Badge>
                  )}
                </div>
                <div className="flex items-end gap-2 flex-wrap">
                  <div className="w-64">
                    <label className="text-[11px] text-slate-500">Tipo di mapping</label>
                    <Select value={currentType}
                      onValueChange={(v) => setDrafts((p) => ({
                        ...p, [q.qid]: { ...(p[q.qid] || m || {}), mapping_type: v,
                          canonical_datapoint_code: currentCode } }))}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`mapping-type-${q.qid}`}
                        aria-label="Tipo di mapping">
                        <SelectValue placeholder="Seleziona…" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(MAPPING_TYPES).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {["EXACT", "PARTIAL", "TRANSFORMABLE", "RELATED"].includes(currentType) && (
                    <div className="flex-1 min-w-[220px]">
                      <label className="text-[11px] text-slate-500">
                        Datapoint canonico (VSME.*/GOV.*/COMPANY.*)
                      </label>
                      <input type="text" value={currentCode}
                        onChange={(e) => setDrafts((p) => ({
                          ...p, [q.qid]: { ...(p[q.qid] || m || {}), mapping_type: currentType,
                            canonical_datapoint_code: e.target.value.trim() } }))}
                        placeholder="es. VSME.B8.HEADCOUNT.TOTAL"
                        className="w-full h-8 rounded-md border border-slate-300 px-2.5 text-xs font-mono"
                        data-testid={`mapping-code-${q.qid}`} aria-label="Datapoint canonico" />
                    </div>
                  )}
                  {currentType === "TRANSFORMABLE" && (
                    <div className="flex gap-1 items-center">
                      <input type="text" placeholder="da (MWh)" maxLength={10}
                        value={draft?.from_unit ?? m?.from_unit ?? ""}
                        onChange={(e) => setDrafts((p) => ({
                          ...p, [q.qid]: { ...(p[q.qid] || m || {}), mapping_type: currentType,
                            canonical_datapoint_code: currentCode, from_unit: e.target.value } }))}
                        className="w-20 h-8 rounded-md border border-slate-300 px-2 text-xs"
                        data-testid={`mapping-from-${q.qid}`} aria-label="Unità origine" />
                      <span className="text-xs text-slate-400">→</span>
                      <input type="text" placeholder="a (kWh)" maxLength={10}
                        value={draft?.to_unit ?? m?.to_unit ?? ""}
                        onChange={(e) => setDrafts((p) => ({
                          ...p, [q.qid]: { ...(p[q.qid] || m || {}), mapping_type: currentType,
                            canonical_datapoint_code: currentCode, to_unit: e.target.value } }))}
                        className="w-20 h-8 rounded-md border border-slate-300 px-2 text-xs"
                        data-testid={`mapping-to-${q.qid}`} aria-label="Unità destinazione" />
                    </div>
                  )}
                  {draft && (
                    <>
                      <Button size="sm" variant="outline" className="h-8 text-xs"
                        disabled={busy || !currentType} onClick={() => saveMapping(q.qid, false)}
                        data-testid={`mapping-save-${q.qid}`}>Salva bozza</Button>
                      <Button size="sm"
                        className="h-8 text-xs bg-green-700 hover:bg-green-800 text-white"
                        disabled={busy || !currentType} onClick={() => saveMapping(q.qid, true)}
                        data-testid={`mapping-verify-${q.qid}`}>
                        <GitBranch className="w-3 h-3 mr-1" aria-hidden="true" />Salva e verifica
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-400" aria-hidden="true" />}
    </div>
  );
}
